import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { API_ROUTES } from "@/config/apiRoutes";
import { loggerService, LogTag } from "@/Utils/LoggerService";
import { appFetch, isApiFailure } from "@/Utils/Services/Fetch/appFetch";

/** What the API hands back: the hosted Stripe page to send the customer to. */
interface CheckoutResponse {
  data?: { url?: string };
}

/**
 * Starts a payment and hands the browser over to Stripe.
 *
 * The redirect is a full page navigation rather than a router push on purpose:
 * the destination is Stripe's own domain, and the customer must come back
 * through the return URLs the backend registered, not through history.
 *
 * `isPending` stays true through the redirect, so the button that triggered it
 * cannot be clicked twice while the browser is on its way out.
 */
const useCheckout = () => {
  const { t } = useTranslation();
  const [isPending, setIsPending] = useState(false);
  /** The same flag, readable synchronously: see `start` below. */
  const pendingRef = useRef(false);

  /** Clears both halves of the pending state, always together. */
  const release = useCallback(() => {
    pendingRef.current = false;
    setIsPending(false);
  }, []);

  useEffect(() => {
    /**
     * Backing out of Stripe restores this page from the back/forward cache
     * with its React state exactly as it was left, so the button that started
     * the redirect would still be spinning, and refuse to be clicked again.
     * `persisted` is what tells a restore apart from a normal load.
     */
    const releaseOnRestore = (event: PageTransitionEvent) => {
      if (event.persisted) {
        release();
      }
    };

    window.addEventListener("pageshow", releaseOnRestore);

    return () => window.removeEventListener("pageshow", releaseOnRestore);
  }, [release]);

  const start = async (route: string, body: Record<string, unknown>) => {
    // Read from a ref, not from state. `isPending` is captured when the render
    // that produced this closure ran, so two clicks landing in the same tick
    // both saw `false` and both opened a checkout, which is how a customer
    // ended up with two Stripe subscriptions. A ref is written synchronously
    // and is therefore true for the second click.
    if (pendingRef.current) {
      return;
    }

    pendingRef.current = true;
    setIsPending(true);

    try {
      const result = (await appFetch(route, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })) as CheckoutResponse | null;

      // `appFetch` already surfaced the reason (403, 402, rate limit…); a
      // missing URL past that point is ours to report.
      if (isApiFailure(result)) {
        release();
        return;
      }

      const url = result?.data?.url;

      if (!url) {
        loggerService.error(LogTag.API, "Checkout returned no URL");
        toast.error(t("billing.checkout_failed"));
        release();
        return;
      }

      loggerService.info(LogTag.API, "Redirecting to Stripe checkout");
      window.location.href = url;
    } catch (error) {
      loggerService.error(LogTag.API, "Checkout could not start", error);
      toast.error(t("billing.checkout_failed"));
      release();
    }
  };

  return {
    isPending,

    /** Subscribe an organisation to `planCode` on `period`. */
    startSubscription: (
      orgId: number,
      planCode: string,
      period: "monthly" | "yearly",
    ) =>
      start(API_ROUTES.billing.checkoutSubscription, {
        org_id: orgId,
        plan_code: planCode,
        period,
      }),

    /**
     * Buy the package covering one event. No organisation is sent: the backend
     * resolves it from the event itself.
     */
    startEventTier: (eventId: number, tierCode: string) =>
      start(API_ROUTES.billing.checkoutEvent, {
        event_id: eventId,
        tier_code: tierCode,
      }),

    /** Open the Stripe billing portal for an organisation. */
    openPortal: (orgId: number) =>
      start(API_ROUTES.billing.portal, { org_id: orgId }),
  };
};

export default useCheckout;
